/**
 * Asset Timeline Component
 * Two modes based on device type:
 * - Beacon: 3 Years / 3 Months toggle, default 3 Months
 * - Gateway: Selectable start date, -1 day / -7 day duration, 6-hour increments
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  TextField,
} from '@mui/material';

import { colorPalette } from '../../theme';
import { formatDateTime } from '../../utils/helpers';

const BEACON_RANGES = {
  '3Y': { label: '3 Years', days: 1095 },
  '3M': { label: '3 Months', days: 90 },
};

const GATEWAY_RANGES = {
  '1D': { label: '-1 Day', days: 1 },
  '7D': { label: '-7 Days', days: 7 },
};

/**
 * Compute waypoint features from asset history for map display.
 */
export const historyToWaypoints = (historyList = []) => {
  if (historyList.length === 0) return [];
  const sorted = [...historyList]
    .filter((h) => h.coordinate?.latitude != null && h.coordinate?.longitude != null)
    .sort((a, b) => {
      const aTs = typeof a.eventTs === 'number' ? a.eventTs : new Date(a.eventTs).getTime();
      const bTs = typeof b.eventTs === 'number' ? b.eventTs : new Date(b.eventTs).getTime();
      return aTs - bTs;
    });
  if (sorted.length === 0) return [];
  return sorted.map((h, idx) => ({
    latitude: h.coordinate.latitude,
    longitude: h.coordinate.longitude,
    timestamp: h.eventTs,
    battery: h.battery,
    rssi: h.rssi,
    type: idx === 0 ? 'start' : idx === sorted.length - 1 ? 'end' : 'path',
  }));
};

const AssetTimeline = ({ historyList = [], onRangeChange, onWaypointsChange, deviceType = 'Beacon' }) => {
  const isGateway = deviceType?.toLowerCase() === 'gateway';
  const ranges = isGateway ? GATEWAY_RANGES : BEACON_RANGES;

  // Auto-select 3Y range if most recent history is older than 90 days
  const computeDefaultRange = () => {
    if (isGateway) return '1D';
    if (historyList.length > 0) {
      const latestTs = Math.max(...historyList.map(h =>
        typeof h.eventTs === 'number' ? h.eventTs : Math.floor(new Date(h.eventTs).getTime() / 1000)
      ));
      const now = Math.floor(Date.now() / 1000);
      const ninetyDaysAgo = now - 90 * 24 * 3600;
      if (latestTs < ninetyDaysAgo) return '3Y';
    }
    return '3M';
  };

  const [range, setRange] = useState(computeDefaultRange());
  const [sliderValue, setSliderValue] = useState(100);
  const [gatewayStartDate, setGatewayStartDate] = useState(new Date().toISOString().split('T')[0]);

  // When history data arrives, auto-switch to 3Y if data is older than 90 days
  useEffect(() => {
    if (!isGateway && historyList.length > 0) {
      const latestTs = Math.max(...historyList.map(h =>
        typeof h.eventTs === 'number' ? h.eventTs : Math.floor(new Date(h.eventTs).getTime() / 1000)
      ));
      const now = Math.floor(Date.now() / 1000);
      const ninetyDaysAgo = now - 90 * 24 * 3600;
      if (latestTs < ninetyDaysAgo && range === '3M') {
        setRange('3Y');
      }
    }
  }, [historyList, isGateway]); // eslint-disable-line react-hooks/exhaustive-deps

  const rangeConfig = ranges[range];

  const handleRangeChange = (_, newRange) => {
    if (newRange !== null) {
      setRange(newRange);
      setSliderValue(100);
      if (onRangeChange) {
        const days = ranges[newRange].days;
        const from = isGateway
          ? new Date(new Date(gatewayStartDate).getTime() - days * 24 * 60 * 60 * 1000)
          : new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        onRangeChange({ from: from.toISOString(), days });
      }
    }
  };

  // Recalculate when gateway start date changes
  useEffect(() => {
    if (isGateway && onRangeChange) {
      const days = rangeConfig.days;
      const from = new Date(new Date(gatewayStartDate).getTime() - days * 24 * 60 * 60 * 1000);
      onRangeChange({ from: from.toISOString(), days });
    }
  }, [gatewayStartDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeWindow = useMemo(() => {
    const baseDate = isGateway ? new Date(gatewayStartDate).getTime() : Date.now();
    const totalMs = rangeConfig.days * 24 * 60 * 60 * 1000;
    const windowStart = baseDate - totalMs;
    const selectedMs = windowStart + (sliderValue / 100) * totalMs;
    return { start: windowStart, end: baseDate, selected: selectedMs };
  }, [rangeConfig, sliderValue, gatewayStartDate, isGateway]);

  const visibleHistory = useMemo(() => {
    return historyList.filter((h) => {
      const ts = typeof h.eventTs === 'number' && h.eventTs < 10000000000
        ? h.eventTs * 1000
        : new Date(h.eventTs).getTime();
      return ts >= timeWindow.start && ts <= timeWindow.selected;
    });
  }, [historyList, timeWindow]);

  const waypoints = useMemo(() => historyToWaypoints(visibleHistory), [visibleHistory]);

  useEffect(() => {
    if (onWaypointsChange) onWaypointsChange(waypoints);
  }, [waypoints, onWaypointsChange]);

  const sliderLabel = useMemo(() => {
    if (sliderValue >= 99) return 'NOW';
    return formatDateTime(timeWindow.selected, 'M/D/YY h:mm A');
  }, [sliderValue, timeWindow.selected]);

  // Build slider marks with time labels at 6-hour intervals
  const sliderMarks = useMemo(() => {
    const totalSegments = rangeConfig.days * 4; // 4 segments per day (6 hours each)
    const baseDate = isGateway ? new Date(gatewayStartDate).getTime() : Date.now();
    const totalMs = rangeConfig.days * 24 * 60 * 60 * 1000;
    const windowStart = baseDate - totalMs;

    const formatHour = (date) => {
      const h = date.getHours();
      if (h === 0) return '12a';
      if (h === 6) return '6a';
      if (h === 12) return '12p';
      if (h === 18) return '6p';
      return '';
    };

    const formatDayHour = (date) => {
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const h = date.getHours();
      if (h === 0) return `${month}/${day}`;
      return formatHour(date);
    };

    if (isGateway) {
      return Array.from({ length: totalSegments + 1 }, (_, i) => {
        const pct = (i / totalSegments) * 100;
        const ts = windowStart + (i / totalSegments) * totalMs;
        const d = new Date(ts);
        const showLabel = range === '1D'
          ? true
          : d.getHours() === 0 || d.getHours() === 12;
        return {
          value: pct,
          label: showLabel ? (range === '1D' ? formatHour(d) : formatDayHour(d)) : '',
        };
      });
    }

    // Beacon mode: show month marks
    const marks = [];
    const startDate = new Date(windowStart);
    const endDate = new Date(baseDate);
    const cursor = new Date(startDate);
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    cursor.setMonth(cursor.getMonth() + 1);
    while (cursor < endDate) {
      const elapsed = cursor.getTime() - windowStart;
      const pct = (elapsed / totalMs) * 100;
      if (pct > 0 && pct < 100) {
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        marks.push({
          value: pct,
          label: range === '3Y' && cursor.getMonth() % 3 !== 0
            ? ''
            : monthNames[cursor.getMonth()],
        });
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return marks;
  }, [rangeConfig, isGateway, gatewayStartDate, range]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: `${colorPalette.backgroundColor}ee`,
        borderTop: `1px solid ${colorPalette.lightSteel}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Gateway: Start date picker */}
        {isGateway && (
          <TextField
            type="date"
            size="small"
            value={gatewayStartDate}
            onChange={(e) => setGatewayStartDate(e.target.value)}
            sx={{ width: 140 }}
            InputProps={{ sx: { fontSize: '0.75rem', height: 30 } }}
          />
        )}

        {/* Range Toggle */}
        <ToggleButtonGroup value={range} exclusive onChange={handleRangeChange} size="small">
          {Object.entries(ranges).map(([key, config]) => (
            <ToggleButton key={key} value={key} sx={{ px: 2, py: 1 }}>
              <Typography variant="caption">{config.label}</Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Timeline Slider with 6-hour segment highlights */}
        <Box sx={{ flexGrow: 1, mx: 2, position: 'relative' }}>
          {/* Alternating segment backgrounds */}
          <Box sx={{ position: 'absolute', top: 8, left: 0, right: 0, height: 8, display: 'flex', borderRadius: 1, overflow: 'hidden', zIndex: 0 }}>
            {Array.from({ length: rangeConfig.days * 4 }, (_, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  backgroundColor: i % 2 === 0
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(255,255,255,0.02)',
                  borderRight: '1px solid rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </Box>
          <Slider
            value={sliderValue}
            onChange={(_, v) => setSliderValue(v)}
            min={0}
            max={100}
            step={isGateway ? 100 / (rangeConfig.days * 4) : 1}
            marks={sliderMarks}
            sx={{
              position: 'relative',
              zIndex: 1,
              mb: 0,  // Remove margin bottom from slider
              color: colorPalette.asseTracOrange,
              '& .MuiSlider-thumb': { width: 14, height: 14 },
              '& .MuiSlider-rail': { opacity: 0.15 },
              '& .MuiSlider-mark': { height: 12, width: 1, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.3)' },
              '& .MuiSlider-markLabel': { fontSize: '0.6rem', top: 28, color: 'text.secondary' },
            }}
          />
          {/* Now label positioned below the slider, aligned with the end */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 0,
              top: 28,
              fontSize: '0.6rem',
              fontWeight: sliderValue >= 99 ? 700 : 400,
              color: sliderValue >= 99 ? colorPalette.asseTracOrange : 'text.secondary',
            }}
          >
            {sliderLabel}
          </Typography>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary">
        {waypoints.length} waypoints visible ({visibleHistory.length} history records)
        {isGateway && ' | Gateway mode: 6-hour increments'}
      </Typography>
    </Paper>
  );
};

export default AssetTimeline;
export { BEACON_RANGES, GATEWAY_RANGES };
